import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { QUERY_KEYS } from '@features/campaigns/shared/consts/queryKeys';
import { submitCampaign } from '@features/campaigns/shared/services/campaign/submitCampaign';
import { ThematicPackageUpsellNavigationState } from '@features/campaigns/shared/types/campaignSubmit.ts';
import { SuccessMessage } from '@shared/components/Toast/SuccessMessage/SuccessMessage';
import { i18nNameSpace } from '@shared/consts/i18n.ts';
import { routePaths } from '@shared/router/routePaths.ts';
import { CampaignId } from '@shared/types/campaign';
import { logError } from '@shared/utils/logError.ts';
import { navigateTo } from '@shared/utils/navigateTo.tsx';

interface SubmitCampaignParams {
  campaignId: CampaignId;
  campaignName: string;
  handleToggleModalVisibility: () => void;
}

export default function useSubmitCampaign({
  campaignId,
  campaignName,
  handleToggleModalVisibility,
}: SubmitCampaignParams) {
  const { t } = useTranslation(i18nNameSpace.API);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { state: locationState, pathname } = useLocation();
  const isSubmitFromCampaignDetails = pathname.includes(routePaths.campaignDetails.url(campaignId));

  const navigateToListOrDetails = () => {
    void navigateTo(navigate, locationState || routePaths.campaigns).catch(logError);
  };

  const refetchCampaignDetails = async () => {
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CAMPAIGN_DETAILS] });
  };

  const navigateToThematicPackageUpsell = (navigationState: ThematicPackageUpsellNavigationState) => {
    void navigateTo(navigate, routePaths.campaignThematicPackage.url(campaignId), {
      state: navigationState,
    }).catch(logError);
  };

  return useMutation({
    mutationFn: () => submitCampaign(campaignId),
    onSuccess: async (response) => {
      handleToggleModalVisibility();
      toast.success(<SuccessMessage message={t('api.success.campaign.submitted', { campaignName })} />);

      const thematicPackageUpsell = response?.thematicPackageUpsell;

      if (thematicPackageUpsell?.isEligible) {
        return navigateToThematicPackageUpsell({
          isUpsellFlowEntryAllowed: true,
          budgetTiers: thematicPackageUpsell.budgetTiers,
          customBudget: thematicPackageUpsell.customBudget,
        });
      }

      if (isSubmitFromCampaignDetails) {
        return await refetchCampaignDetails();
      }

      return navigateToListOrDetails();
    },
  });
}
